import React from 'react';
import { RefreshCcw, AlertTriangle } from 'lucide-react';

class GlobalErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error("Global Error Boundary Caught:", error, errorInfo);
        this.setState({ errorInfo });
    }

    handleReload = () => {
        window.location.href = '/';
    }

    handleReport = () => {
        window.location.href = '/?tab=report-issue';
    }

    render() {
        if (this.state.hasError) {
            // Main Container
            return React.createElement('div', {
                style: {
                    height: '100vh',
                    width: '100vw',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#0f172a',
                    color: '#e2e8f0',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    textAlign: 'center',
                    padding: '20px',
                    boxSizing: 'border-box'
                }
            },
                // Mascot Image
                React.createElement('img', {
                    src: "/images/mascots/BitGlitch.png",
                    alt: "Application Crashed",
                    style: {
                        maxWidth: '250px',
                        height: 'auto',
                        marginBottom: '30px',
                        filter: 'drop-shadow(0 0 20px rgba(236, 72, 153, 0.4))'
                    }
                }),

                // Title
                React.createElement('h1', {
                    style: {
                        fontSize: '36px',
                        fontWeight: '800',
                        marginBottom: '10px',
                        background: 'linear-gradient(135deg, #f472b6 0%, #ec4899 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                    }
                }, "System Critical Failure"),

                // Message
                React.createElement('p', {
                    style: {
                        maxWidth: '500px',
                        marginBottom: '40px',
                        lineHeight: '1.6',
                        color: '#94a3b8'
                    }
                }, "Our internal navigation systems have encountered an unexpected anomaly. The application state has been compromised and requires a reset."),

                // Button Container
                React.createElement('div', {
                    style: { display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }
                },
                    // Reload Button
                    React.createElement('button', {
                        onClick: this.handleReload,
                        style: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '12px 24px',
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'transform 0.2s',
                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                        },
                        onMouseOver: (e) => e.currentTarget.style.transform = 'translateY(-2px)',
                        onMouseOut: (e) => e.currentTarget.style.transform = 'translateY(0)'
                    },
                        React.createElement(RefreshCcw, { size: 20 }),
                        "Reboot System"
                    ),

                    // Report Button
                    React.createElement('button', {
                        onClick: this.handleReport,
                        style: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '12px 24px',
                            background: 'rgba(255,255,255,0.1)',
                            color: 'white',
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '12px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'background 0.2s'
                        },
                        onMouseOver: (e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)',
                        onMouseOut: (e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'
                    },
                        React.createElement(AlertTriangle, { size: 20 }),
                        "Report Anomaly"
                    )
                ),

                // Developer Details
                this.state.error && React.createElement('details', {
                    style: { marginTop: '50px', maxWidth: '600px', textAlign: 'left', opacity: 0.5 }
                },
                    React.createElement('summary', { style: { cursor: 'pointer', listStyle: 'none' } }, "Technical Details"),
                    React.createElement('pre', {
                        style: {
                            background: 'rgba(0,0,0,0.3)',
                            padding: '15px',
                            borderRadius: '8px',
                            overflow: 'auto',
                            fontSize: '12px',
                            marginTop: '10px'
                        }
                    },
                        this.state.error.toString(),
                        React.createElement('br'),
                        this.state.errorInfo?.componentStack
                    )
                )
            );
        }

        return this.props.children;
    }
}

export default GlobalErrorBoundary;
